import { DependencyList, useEffect, useRef, useState } from 'react';

type AsyncFactory<T> = () => Promise<T>;
type CancellableAsyncFactory<T> = AsyncFactory<T> & {
  running?: boolean;
  cancelled?: boolean;
};

export const useMemoAsync = <T>(
  factory: AsyncFactory<T>,
  deps: DependencyList | undefined
) => {
  const [result, setResult] = useState<T | undefined>(undefined);
  const refs = useRef({
    factory,
  });
  useEffect(() => {
    // only reset the factory ref if the deps change
    refs.current.factory = factory;
    // build a "task" to run
    const task: CancellableAsyncFactory<T> = async function () {
      if (task.cancelled) return undefined;
      task.running = true;
      const _result = await refs.current.factory();
      task.running = false;
      return _result;
    } as CancellableAsyncFactory<T>;

    // execute the task asynchronously
    //  if the work finishes and the task not cancelled,
    //  then update the result state
    (async () => {
      const _r = await task();
      if (!task.cancelled) {
        setResult(_r);
      }
    })();

    // if the effect is dismounted,
    //  cancel the last task
    return () => {
      task.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...(deps ?? [])]);

  // return the current result state value
  return result;
};
